// Hover definitions for every metric the dashboard names.
//
// One string per metric, shared by the card, the drill-down column and the
// legend entry. A metric that appears in three places must not explain itself
// three different ways -- and the abbreviated column headers ("Avail %",
// "Hrs/DVM/wk") are exactly where a definition earns the most, since the label
// itself has no room to carry one.
//
// The pairs below are the ones supplied for the dashboard; wording is theirs
// verbatim. Elements deliberately left without a definition: Completed Appts,
// Avg Appt Time, vs. PY and Total days/wk.
//
// Two of these describe the metric as it is DEFINED rather than as the source
// currently computes it -- overlapping visits are not yet deduped, so seen time
// double-counts concurrent appointments and the four utilization segments can
// exceed 100% under an appointment-type filter. That is a known source-side fix
// in flight; the text here is the definition it will match.

export const METRIC_TOOLTIPS = {
  // ── Utilization, as time ───────────────────────────────────────────────────
  worked: "All the time a DVM actually worked, including visits seen outside scheduled hours.",
  seen: "Time spent with pets in completed visits. Overlapping visits count once, by the clock.",
  blockoff: "Scheduled time intentionally closed to pets, such as lunch, training, or meetings.",
  available: "Open scheduled time with no appointment and no block-off. Capacity that went unused.",
  // Only rendered under an appointment-type filter. Not one of the supplied
  // pairs -- it exists to explain why a fourth segment appears at all.
  otherTypes:
    "Pet-seen time in appointment types outside the current type filter. Shown so the segments still add up to worked time.",

  // ── Utilization, as a share of worked time ─────────────────────────────────
  seenPct: "Share of worked time spent seeing pets. Seen, block-off, and available add up to 100%.",
  blockoffPct: "Share of worked time closed to pets. A high value flags too much time blocked off.",
  availablePct: "Share of worked time left open. A high value flags unused capacity.",

  // ── Scheduled capacity ─────────────────────────────────────────────────────
  hoursPerDvmPerWeek:
    "Average hours a DVM is scheduled to work each week over the selected period. Calendar periods (e.g., MTD) may include a partial first or last week.",
  hoursPerDvmPerDay:
    "Average scheduled hours per DVM on the days they actually work. Days off are not counted.",
  daysPerDvmPerWeek:
    "Average number of days per week a DVM is scheduled to work. Calendar periods (e.g., MTD) may include a partial first or last week.",
  dvmsPerDayPerSite: "Average number of DVMs scheduled on a typical day the site is open.",
  totalHoursPerWeek: "Total scheduled DVM hours across the selected scope, shown per week.",
  totalHours: "Total scheduled DVM hours across the selected scope and period.",

  // ── Revenue ────────────────────────────────────────────────────────────────
  medNetRevenue:
    "Net revenue from in-clinic medical care, after discounts, tax, and returns. Excludes wellness for now.",
  revPerSeenHour: "Medical net revenue earned for each hour actually spent seeing pets.",
  revPerScheduledHour:
    "Medical net revenue earned for each scheduled hour, whether or not it was used.",
  revPerCompletedAppt: "Average medical net revenue per completed appointments",
  completedApptsPerDvmPerDay:
    "Average completed visits per DVM on the days they work. Lower than med visits per DVM day which includes tech-served visits.",
} as const;

export type MetricTooltipKey = keyof typeof METRIC_TOOLTIPS;
